import flatpickr from "flatpickr";
import IYearDropdownPluginOptions from "./Interfaces/IYearDropdownPluginOptions";
import IYearDropdownPluginOptionsOptional from "./Interfaces/IYearDropdownPluginOptionsOptional";

export default class FlatpickrYearDropdownPlugin {
    private readonly defaultYearBound: number = 125;

    private pluginConfig: IYearDropdownPluginOptions | null = {
        text: null,
        date: null,
        yearBound: null,
    };

    public init(): (pluginConfig: IYearDropdownPluginOptionsOptional) => any {
        const yearDropdownPlugin = (pluginConfig: IYearDropdownPluginOptionsOptional) => {
            this.parseConfigOptions(pluginConfig);

            const getYear = (value: Date) => {
                return value.getFullYear();
            };

            const selectedYear = getYear(this.pluginConfig?.date as Date);

            return (fp: flatpickr.Instance & {yearSelectContainer: HTMLElement}) => {
                if (fp.isMobile) {
                    return;
                }

                this.extendFlatpickrSet(fp);

                fp.yearSelectContainer = fp._createElement(
                    "div",
                    "flatpickr-year-select",
                    this.pluginConfig?.text?.toString(),
                );
                let currentYearDropdown: HTMLSelectElement;

                fp.yearSelectContainer.tabIndex = -1;
                currentYearDropdown = this.createSelectElement(fp, selectedYear);

                const yearPropagationHandler = (evt: Event) => {
                    const target = evt.target as HTMLSelectElement;
                    const year = parseInt(target.value, 10);
                    fp.changeYear(year);
                };

                currentYearDropdown.addEventListener("change", yearPropagationHandler);

                const callback = (newSelect: HTMLSelectElement) => {
                    currentYearDropdown = newSelect;
                };

                this.regenerateOptions(fp, yearPropagationHandler, currentYearDropdown, callback);

                fp.yearSelectContainer.appendChild(currentYearDropdown);

                return {
                    onReady(): void {
                        const name = fp.monthNav.className;
                        const yearInputCollection = fp.calendarContainer.getElementsByClassName(name);
                        const el = yearInputCollection[0];
                        el.parentNode?.insertBefore(fp.yearSelectContainer, el.parentNode?.firstChild);
                    },
                    onYearChange(dObj: object, dStr: string, fpi: flatpickr.Instance): void {
                        const currentYear = fpi.currentYear as number;
                        currentYearDropdown.value = currentYear.toString();
                    },
                };
            };
        };

        // @ts-ignore
        return yearDropdownPlugin;
    }

    private parseConfigOptions(pluginConfig: IYearDropdownPluginOptionsOptional): void {
        const defaultConfig: IYearDropdownPluginOptions = {
            text: "",
            date: new Date(),
            yearBound: this.defaultYearBound,
        };

        this.pluginConfig = {
            ...pluginConfig,
            ...defaultConfig,
        } as IYearDropdownPluginOptions;
    }

    private createSelectElement(fpi: flatpickr.Instance , year: number): HTMLSelectElement {
        const yearDropdown = document.createElement("select");
        yearDropdown.classList.add("flatpickr-year-dropdown");

        this.limitMinMaxDates(fpi);
        const start: number = fpi.config.minDate?.getFullYear() ?? new Date().getFullYear();
        const end: number = fpi.config.maxDate?.getFullYear() ?? new Date().getFullYear();
        if (end - start <= 1) {
            yearDropdown.disabled = true;
        }
        for (let i = start; i <= end; i++) {
            const option = document.createElement("option");
            option.value = String(i);
            option.text = String(i);
            yearDropdown.appendChild(option);
        }

        yearDropdown.value = year.toString();

        return yearDropdown;
    }

    private limitMinMaxDates(fpInstance: any): void {
        const now = new Date();
        const currentDay = now.getDate();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const start = fpInstance.config.minDate;
        if (!start) {
            fpInstance.config.minDate =
                new Date(currentYear - (this?.pluginConfig?.yearBound ?? 0), currentMonth, currentDay);
        }

        const end = fpInstance.config.maxDate;
        if (!end) {
            fpInstance.config.maxDate =
                new Date(currentYear + (this?.pluginConfig?.yearBound ?? 0), currentMonth, currentDay);
        }
    }

    private extendFlatpickrSet(fpInstance: any): void {
        const origSetFunction = fpInstance.set as (...options: [number]) => {};

        const newSetFunction = (...options: [number]) => {
            const property = options[0];

            origSetFunction(...options);
            const event = this.createNewEvent(`flatpickr:change:${property}`);
            fpInstance.element.dispatchEvent(event);
        };

        fpInstance.set = newSetFunction;
    }

    public replaceSelectElement(fpInstance: any,
                                yearContainer: HTMLElement,
                                currentChild: HTMLSelectElement,
                                eventHandler: (evt: Event) => void): HTMLSelectElement {
        const currentYear = fpInstance.currentYear as number;
        const selectEl = this.createSelectElement(fpInstance, currentYear);
        currentChild.removeEventListener("change", eventHandler);
        yearContainer.replaceChild(selectEl, currentChild);
        selectEl.addEventListener("change", eventHandler);

        return selectEl;
    }

    private regenerateOptions(fpInstance: any,
                              eventHandler: (evt: Event) => void,
                              currentYearDropdown: HTMLSelectElement,
                              newValueCallback: (newSelect: HTMLSelectElement) => void): void {
        const yearContainer = fpInstance.yearSelectContainer as HTMLElement;

        let curYearDropdown = currentYearDropdown;

        fpInstance.element.addEventListener("flatpickr:change:minDate",
            () => {
                const newSelect =
                    this.replaceSelectElement(fpInstance, yearContainer, curYearDropdown, eventHandler);
                curYearDropdown = newSelect;
                newValueCallback(newSelect);

                this.toggleMonthSelectorEnabled(fpInstance);
            });

        fpInstance.element.addEventListener("flatpickr:change:maxDate",
            () => {
                const newSelect =
                    this.replaceSelectElement(fpInstance, yearContainer, curYearDropdown, eventHandler);
                curYearDropdown = newSelect;
                newValueCallback(newSelect);

                this.toggleMonthSelectorEnabled(fpInstance);
            });
    }

    /**
     * Workaround for regenerating months selector options
     * @param fp Flatpickr instance
     */
    public toggleMonthSelectorEnabled(fp: flatpickr.Instance): void {
        fp.set("showMonths", true);
    }

    /**
     * Event polyfill for IE
     * @param eventName Name of the event
     */
    private createNewEvent(eventName: string, bubbles = false, cancelable = false): Event {
        let event;
        if (typeof (Event) === "function") {
            event = new Event(eventName, {bubbles, cancelable});
        } else {
            event = document.createEvent("Event");
            event.initEvent(eventName, bubbles, cancelable);
        }

        return event;
    }
}
